import {
  Add as AddIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
  ContactPhone as ContactPhoneIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  PhotoCamera as PhotoCameraIcon,
  School as SchoolIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TextField,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { TableSkeleton } from '../../../components/TableSkeleton';
import useCenterStudents from '../../../hooks/useCenterStudents';
import { truncateFileName } from '../../../utils/truncateFileName';

const BRAND = '#454751';
const BRAND_ALPHA = '#454751d1';

/* ── Shared field sx — applied to every text input inside the form ── */
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontSize: 14,
    background: '#ffffff',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '& fieldset': { borderColor: '#d0d0d0', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: BRAND },
    '&.Mui-focused fieldset': {
      borderColor: BRAND,
      borderWidth: '2px',
      boxShadow: `0 0 0 3px ${BRAND}18`,
    },
    '&.Mui-error fieldset': { borderColor: '#d32f2f' },
  },
  '& .MuiInputLabel-root': {
    fontSize: 14,
    color: '#777',
    '&.Mui-focused': { color: BRAND },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.78)',
      backgroundColor: '#ffffff',
      padding: '0 5px',
      borderRadius: '4px',
      fontWeight: 600,
    },
  },
  '& .MuiFormHelperText-root': { fontSize: 11, mt: 0.4 },
};

/* ── Styled TextField used only inside this form ─────────────────── */
const FormTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontSize: 14,
    background: '#ffffff',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '& fieldset': { borderColor: '#d0d0d0', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: BRAND },
    '&.Mui-focused fieldset': {
      borderColor: BRAND,
      borderWidth: '2px',
      boxShadow: `0 0 0 3px ${BRAND}18`,
    },
    '&.Mui-error fieldset': { borderColor: '#d32f2f' },
  },
  '& .MuiInputLabel-root': {
    fontSize: 14,
    color: '#777',
    '&.Mui-focused': { color: BRAND },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.78)',
      backgroundColor: '#ffffff',
      padding: '0 5px',
      borderRadius: '4px',
      fontWeight: 600,
    },
  },
  '& .MuiFormHelperText-root': { fontSize: 11, marginTop: 3 },
}));

/* ── Thin wrapper so existing register() spread still works ──────── */
const FormInput = React.forwardRef(({ label, error, helperText, type = 'text', multiline, rows, InputLabelProps, ...rest }, ref) => (
  <FormTextField
    fullWidth
    label={label}
    type={type}
    multiline={multiline}
    rows={rows}
    InputLabelProps={InputLabelProps}
    error={!!error}
    helperText={helperText}
    inputRef={ref}
    {...rest}
  />
));
FormInput.displayName = 'FormInput';

const TITLES = ['Mr', 'Mrs', 'Miss', 'Master', 'Dr', 'Prof'];
const RELATIONSHIPS = ['Father', 'Mother', 'Guardian', 'Self', 'Spouse'];
const GENDERS = ['Male', 'Female', 'Other'];
const EDUCATION_OPTIONS = [
  'Below 10th', '10th (Matriculation)', '12th (Intermediate)',
  'Diploma', 'ITI', 'Graduate (UG)', 'Post Graduate (PG)', 'PhD', 'Other',
];
const CATEGORIES = ['General', 'OBC', 'OBC-NCL', 'SC', 'ST', 'EWS', 'Other'];
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman & Diu',
  'Delhi (NCT)', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

/* ── Section header with icon ─────────────────────────────────────── */
const SectionHeader = ({ title, icon: Icon, color = BRAND_ALPHA }) => (
  <Grid item size={{ xs: 12 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mt: 2,
        mb: 1,
        px: 2,
        py: 1,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      {Icon && <Icon sx={{ color, fontSize: 20 }} />}
      <Typography fontWeight={700} fontSize={14} sx={{ color, textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {title}
      </Typography>
    </Box>
  </Grid>
);

/* ── Image upload zone with live preview ──────────────────────────── */
const ImageUploadZone = ({ id, label, accept, register: reg, watch: watchFn, onRemove, required, error, existingUrl, aspectHint }) => {
  const currentFile = watchFn(id);
  const hasFile = currentFile && currentFile[0]?.name;
  const previewUrl = hasFile ? URL.createObjectURL(currentFile[0]) : null;
  const displayUrl = previewUrl || existingUrl;

  return (
    <Box>
      <Typography fontSize={13} fontWeight={600} sx={{ color: '#555', mb: 1 }}>
        {label}
        {aspectHint && <Typography component="span" fontSize={11} color="textSecondary"> &nbsp;({aspectHint})</Typography>}
      </Typography>
      <input
        type="file" id={id} accept={accept}
        {...reg(id, required ? {
          validate: (v) => (v && v.length > 0) || existingUrl || `${label} is required`,
        } : {})}
        style={{ display: 'none' }}
      />
      <Box
        onClick={() => !hasFile && document.getElementById(id)?.click()}
        sx={{
          border: `2px dashed ${error ? '#d32f2f' : hasFile ? BRAND_ALPHA : '#bdbdbd'}`,
          borderRadius: 2,
          p: displayUrl ? 0 : 2,
          cursor: hasFile ? 'default' : 'pointer',
          textAlign: 'center',
          transition: 'border-color 0.2s, background 0.2s',
          background: displayUrl ? 'transparent' : hasFile ? '#f0f4ff' : '#fafafa',
          '&:hover': !hasFile ? { borderColor: BRAND_ALPHA, background: '#f5f5f5' } : {},
          overflow: 'hidden',
          minHeight: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {displayUrl ? (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <img
              src={displayUrl}
              alt="preview"
              style={{ maxHeight: 110, maxWidth: '100%', display: 'block', margin: '0 auto', borderRadius: 6 }}
            />
            {hasFile && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const input = document.getElementById(id);
                  if (input) input.value = '';
                  onRemove(id);
                }}
                sx={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { background: 'rgba(0,0,0,0.8)' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            {!hasFile && existingUrl && (
              <Box
                onClick={(e) => { e.stopPropagation(); document.getElementById(id)?.click(); }}
                sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(0,0,0,0)', borderRadius: 6, cursor: 'pointer',
                  '&:hover': { background: 'rgba(0,0,0,0.4)', '& .change-label': { opacity: 1 } },
                  transition: 'background 0.2s' }}
              >
                <Typography className="change-label" fontSize={12} color="#fff" fontWeight={700}
                  sx={{ opacity: 0, transition: 'opacity 0.2s', background: 'rgba(0,0,0,0.5)', px: 1.5, py: 0.5, borderRadius: 1 }}>
                  Click to Change
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ color: '#bdbdbd', fontSize: 32, mb: 0.5 }} />
            <Typography fontSize={12} color="textSecondary">Click to upload</Typography>
          </Box>
        )}
      </Box>
      {error && <Typography fontSize={11} color="error" sx={{ mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
};

/* ── Generic file upload (for docs/sign) ─────────────────────────── */
const DocUploadZone = ({ id, label, accept, register: reg, watch: watchFn, onRemove, required, error, existingName }) => {
  const currentFile = watchFn(id);
  const hasFile = currentFile && currentFile[0]?.name;

  return (
    <Box>
      <Typography fontSize={13} fontWeight={600} sx={{ color: '#555', mb: 1 }}>{label}</Typography>
      <input
        type="file" id={id} accept={accept}
        {...reg(id, required ? { required: `${label} is required`, validate: (v) => (v && v.length > 0) || `${label} is required` } : {})}
        style={{ display: 'none' }}
      />
      <Box
        sx={{
          border: `2px dashed ${error ? '#d32f2f' : hasFile ? BRAND_ALPHA : '#bdbdbd'}`,
          borderRadius: 2,
          px: 2, py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: hasFile ? '#f0f9f0' : '#fafafa',
          cursor: hasFile ? 'default' : 'pointer',
          '&:hover': !hasFile ? { borderColor: BRAND_ALPHA, background: '#f5f5f5' } : {},
          transition: 'all 0.2s',
        }}
        onClick={() => !hasFile && document.getElementById(id)?.click()}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CloudUploadIcon sx={{ color: hasFile ? '#2e7d32' : '#bdbdbd', fontSize: 22 }} />
          <Typography fontSize={13} color={hasFile ? '#2e7d32' : 'textSecondary'}>
            {hasFile
              ? truncateFileName(currentFile[0].name)
              : existingName
              ? `Current: ${truncateFileName(existingName)}`
              : 'Click to choose file'}
          </Typography>
        </Box>
        {hasFile && (
          <IconButton size="small" onClick={(e) => {
            e.stopPropagation();
            const input = document.getElementById(id);
            if (input) input.value = '';
            onRemove(id);
          }} sx={{ color: '#d32f2f' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {error && <Typography fontSize={11} color="error" sx={{ mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
};

/* ── Styled Select field ──────────────────────────────────────────── */
const selectSx = {
  borderRadius: '10px',
  fontSize: 14,
  background: '#ffffff',
  height: 56,
  transition: 'box-shadow 0.2s',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0', borderWidth: '1.5px' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: BRAND,
    borderWidth: '2px',
  },
  '&.Mui-focused': { boxShadow: `0 0 0 3px ${BRAND}18` },
};

const SelectField = ({ name, label, options, control, required, error }) => (
  <FormControl fullWidth variant="outlined" error={!!error}>
    <InputLabel sx={{
      fontSize: 14,
      color: '#777',
      '&.Mui-focused': { color: BRAND },
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.78)',
        backgroundColor: '#ffffff',
        padding: '0 5px',
        borderRadius: '4px',
        fontWeight: 600,
      },
    }}>
      {label}
    </InputLabel>
    <Controller
      name={name}
      control={control}
      rules={required ? { required: `${label} is required` } : {}}
      render={({ field }) => (
        <Select {...field} label={label} sx={selectSx}>
          {options.map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: 14 }}>{opt}</MenuItem>
          ))}
        </Select>
      )}
    />
    {error && <FormHelperText sx={{ fontSize: 11 }}>{error}</FormHelperText>}
  </FormControl>
);

/* ── Detail row for view dialog ──────────────────────────────────── */
const DetailRow = ({ label, value, highlight }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      py: 0.8,
      px: 1,
      borderRadius: 1,
      mb: 0.5,
      background: highlight ? '#f9f9f9' : 'transparent',
    }}
  >
    <Typography fontSize={13} fontWeight={600} color="textSecondary" sx={{ minWidth: 150 }}>{label}</Typography>
    <Typography fontSize={13} textAlign="right" fontWeight={value ? 500 : 400} color={value ? 'text.primary' : 'text.disabled'}>
      {value || '—'}
    </Typography>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                      */
/* ═══════════════════════════════════════════════════════════════════ */
const CenterStudentManagement = () => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [deleteStudentId, setDeleteStudentId] = useState(null);
  const [viewStudent, setViewStudent] = useState(null);

  const { students, isStudentsLoading, addStudent, isAddingStudent, updateStudent, isUpdatingStudent, deleteStudent, isDeletingStudent } = useCenterStudents();

  const { register, handleSubmit, control, watch, setValue, reset, clearErrors, formState: { errors } } = useForm({ reValidateMode: 'onChange' });

  const handleOpenAdd = () => {
    reset({
      title: '', applicantName: '', relationship: '', guardianName: '', motherName: '',
      gender: '', dateOfBirth: '', photo: null, sign: null, course: '', batch: '',
      fees: '', dateOfAdmission: format(new Date(), 'yyyy-MM-dd'), email: '', mobile: '',
      mobileGuardian: '', aadhaarNumber: '', education: '', document: null,
      category: '', address: '', state: '', district: '', pinCode: '',
    });
    clearErrors();
    setIsEditing(false);
    setEditingStudent(null);
    setOpen(true);
  };

  const handleOpenEdit = (student) => {
    reset({
      title: student.title || '', applicantName: student.applicantName || '',
      relationship: student.relationship || '', guardianName: student.guardianName || '',
      motherName: student.motherName || '', gender: student.gender || '',
      dateOfBirth: student.dateOfBirth ? format(new Date(student.dateOfBirth), 'yyyy-MM-dd') : '',
      photo: null, sign: null,
      course: student.course || '', batch: student.batch || '',
      fees: student.fees || '',
      dateOfAdmission: student.dateOfAdmission ? format(new Date(student.dateOfAdmission), 'yyyy-MM-dd') : '',
      email: student.email || '', mobile: student.mobile || '',
      mobileGuardian: student.mobileGuardian || '', aadhaarNumber: student.aadhaarNumber || '',
      education: student.education || '', document: null, category: student.category || '',
      address: student.address || '', state: student.state || '',
      district: student.district || '', pinCode: student.pinCode || '',
    });
    clearErrors();
    setIsEditing(true);
    setEditingStudent(student);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSave = async (values) => {
    try {
      const formData = new FormData();
      const fileKeys = ['photo', 'sign', 'document'];
      Object.entries(values).forEach(([key, val]) => {
        if (!fileKeys.includes(key) && val !== null && val !== undefined) {
          formData.append(key, val);
        }
      });
      if (values.photo?.[0]) formData.append('photo', values.photo[0]);
      if (values.sign?.[0]) formData.append('sign', values.sign[0]);
      if (values.document?.[0]) formData.append('document', values.document[0]);

      if (isEditing) {
        formData.append('_id', editingStudent._id);
        await updateStudent(formData);
        toast.success('Student updated successfully');
      } else {
        await addStudent(formData);
        toast.success('Student added successfully');
      }
      handleClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Something went wrong');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteStudent(deleteStudentId);
      toast.success('Student deleted');
      setDeleteStudentId(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Something went wrong');
    }
  };

  if (isStudentsLoading) return <TableSkeleton />;

  return (
    <Box>
      {/* ── Page Header ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, pb: 2, borderBottom: `2px solid ${BRAND_ALPHA}20`,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: BRAND }}>
            Student Registrations
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Manage all student enrollment records
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          sx={{
            background: `linear-gradient(135deg, ${BRAND} 0%, #6b6d75 100%)`,
            fontSize: 14, px: 3, py: 1.2, borderRadius: 2,
            boxShadow: '0 4px 12px rgba(69,71,81,0.35)',
            '&:hover': { boxShadow: '0 6px 16px rgba(69,71,81,0.5)' },
          }}
        >
          New Student
        </Button>
      </Box>

      {/* ── Table ───────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: `linear-gradient(to right, ${BRAND}f0, #6b6d75f0)` }}>
                {['Photo', 'Applicant', 'Course', 'Mobile', 'Admission', 'Category', 'Actions'].map((h) => (
                  <TableCell key={h} align={h === 'Actions' ? 'right' : 'left'}
                    sx={{ color: '#fff', fontWeight: 700, fontSize: 13, py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {students.length > 0 ? students.map((s, i) => (
                <TableRow
                  key={s._id}
                  sx={{
                    background: i % 2 === 0 ? '#fff' : '#f9f9fb',
                    '&:hover': { background: `${BRAND_ALPHA}0d` },
                    transition: 'background 0.15s',
                    '& td': { fontSize: 14, py: 1.2 },
                  }}
                >
                  <TableCell>
                    <Avatar
                      src={s.photo?.url}
                      alt={s.applicantName}
                      sx={{ width: 40, height: 40, border: `2px solid ${BRAND_ALPHA}40` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600} fontSize={14}>{s.title} {s.applicantName}</Typography>
                    <Typography fontSize={12} color="textSecondary">{s.email}</Typography>
                  </TableCell>
                  <TableCell>
                  <Chip
                    label={s.course || '—'}
                    size="small"
                    sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 12 }}
                  />
                  </TableCell>
                  <TableCell>{s.mobile}</TableCell>
                  <TableCell>
                    {s.dateOfAdmission ? new Date(s.dateOfAdmission).toLocaleDateString('en-IN') : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={s.category} size="small" variant="outlined" sx={{ fontSize: 12 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton onClick={() => setViewStudent(s)}
                        sx={{ color: '#1976d2', '&:hover': { background: '#e3f2fd' }, p: 1 }}>
                        <ViewIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleOpenEdit(s)}
                        sx={{ color: BRAND_ALPHA, '&:hover': { background: `${BRAND_ALPHA}15` }, p: 1 }}>
                        <EditIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => setDeleteStudentId(s._id)}
                        sx={{ color: '#d32f2f', '&:hover': { background: '#ffebee' }, p: 1 }}>
                        <DeleteIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <SchoolIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography variant="h6" color="textSecondary">No students yet</Typography>
                    <Typography variant="body2" color="textSecondary">Click "New Student" to add the first registration</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ═══════════════════════════════════════════════════════════
           ADD / EDIT DIALOG
          ═══════════════════════════════════════════════════════════ */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3 } }}>
        <form onSubmit={handleSubmit(handleSave)}>

          {/* Dialog Header */}
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${BRAND} 0%, #6b6d75 100%)`,
              color: '#fff', py: 2, px: 3,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1.5}>
                <SchoolIcon sx={{ fontSize: 26 }} />
                <Box>
                  <Typography fontWeight={700} fontSize={18} lineHeight={1.2}>
                    {isEditing ? 'Edit Student Registration' : 'New Student Registration'}
                  </Typography>
                  <Typography fontSize={12} sx={{ opacity: 0.8 }}>
                    Fill in all required fields marked with *
                  </Typography>
                </Box>
              </Box>
              <IconButton sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.15)' } }} onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ maxHeight: '74vh', overflowY: 'auto', p: 3, background: '#f8f9fa' }}>
            <Grid container spacing={2.5}>

              {/* ── Personal Details ──────────────────────────── */}
              <SectionHeader title="Personal Details" icon={PersonIcon} />

              <Grid item size={{ xs: 12, sm: 3 }}>
                <SelectField name="title" label="Title *" options={TITLES} control={control} required error={errors.title?.message} />
              </Grid>
              <Grid item size={{ xs: 12, sm: 9 }}>
                <FormInput
                  {...register('applicantName', { required: 'Applicant name is required' })}
                  label="Applicant Full Name *"
                  error={!!errors.applicantName} helperText={errors.applicantName?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <SelectField name="relationship" label="Relationship *" options={RELATIONSHIPS} control={control} required error={errors.relationship?.message} />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('guardianName', { required: 'Guardian name is required' })}
                  label="Guardian Name *"
                  error={!!errors.guardianName} helperText={errors.guardianName?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('motherName', { required: "Mother's name is required" })}
                  label="Mother Name *"
                  error={!!errors.motherName} helperText={errors.motherName?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <SelectField name="gender" label="Gender *" options={GENDERS} control={control} required error={errors.gender?.message} />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('dateOfBirth', { required: 'Date of birth is required' })}
                  label="Date of Birth *" type="date"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.dateOfBirth} helperText={errors.dateOfBirth?.message}
                />
              </Grid>

              {/* ── Photo & Signature ─────────────────────────── */}
              <SectionHeader title="Photo & Signature" icon={PhotoCameraIcon} color="#1565c0" />

              <Grid item size={{ xs: 12, sm: 4 }}>
                <ImageUploadZone
                  id="photo" label="Student Photo *" accept="image/*"
                  register={register} watch={watch}
                  onRemove={(id) => setValue(id, null)}
                  required={!isEditing}
                  error={errors.photo?.message}
                  existingUrl={isEditing ? editingStudent?.photo?.url : null}
                  aspectHint="250×250 preferred"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <ImageUploadZone
                  id="sign" label="Signature (optional)" accept="image/*"
                  register={register} watch={watch}
                  onRemove={(id) => setValue(id, null)}
                  required={false}
                  existingUrl={isEditing ? editingStudent?.sign?.url : null}
                  aspectHint="250×100 preferred"
                />
              </Grid>

              {/* ── Course & Admission ────────────────────────── */}
              <SectionHeader title="Course & Admission" icon={SchoolIcon} color="#2e7d32" />

              <Grid item size={{ xs: 12, sm: 6 }}>
                <FormInput
                  {...register('course', { required: 'Course name is required' })}
                  label="Course Name *"
                  error={!!errors.course} helperText={errors.course?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <FormInput {...register('batch')} label="Batch (optional)" />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <FormInput
                  {...register('fees', { required: 'Fees is required' })}
                  label="Fees (₹) *" type="number"
                  error={!!errors.fees} helperText={errors.fees?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <FormInput
                  {...register('dateOfAdmission', { required: 'Date of admission is required' })}
                  label="Date of Admission *" type="date"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.dateOfAdmission} helperText={errors.dateOfAdmission?.message}
                />
              </Grid>

              {/* ── Contact Information ───────────────────────── */}
              <SectionHeader title="Contact Information" icon={ContactPhoneIcon} color="#e65100" />

              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email address' } })}
                  label="Email *" type="email"
                  error={!!errors.email} helperText={errors.email?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('mobile', { required: 'Mobile is required', pattern: { value: /^[0-9]{10}$/, message: 'Enter a valid 10-digit number' } })}
                  label="Mobile Number *"
                  error={!!errors.mobile} helperText={errors.mobile?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('mobileGuardian', { required: 'Guardian mobile is required', pattern: { value: /^[0-9]{10}$/, message: 'Enter a valid 10-digit number' } })}
                  label="Guardian Mobile *"
                  error={!!errors.mobileGuardian} helperText={errors.mobileGuardian?.message}
                />
              </Grid>

              {/* ── Identity & Education ──────────────────────── */}
              <SectionHeader title="Identity & Education" icon={BadgeIcon} color="#6a1b9a" />

              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('aadhaarNumber', { required: 'Aadhaar number is required', pattern: { value: /^[0-9]{12}$/, message: 'Must be 12 digits' } })}
                  label="Aadhaar Number *"
                  error={!!errors.aadhaarNumber} helperText={errors.aadhaarNumber?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <SelectField name="education" label="Education *" options={EDUCATION_OPTIONS} control={control} required error={errors.education?.message} />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <SelectField name="category" label="Category *" options={CATEGORIES} control={control} required error={errors.category?.message} />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <DocUploadZone
                  id="document" label="Student Document (optional)"
                  accept="image/*,application/pdf"
                  register={register} watch={watch}
                  onRemove={(id) => setValue(id, null)}
                  required={false}
                  existingName={isEditing ? editingStudent?.document?.name : null}
                />
              </Grid>

              {/* ── Address Details ───────────────────────────── */}
              <SectionHeader title="Address Details" icon={HomeIcon} color="#00695c" />

              <Grid item size={{ xs: 12 }}>
                <FormInput
                  {...register('address', { required: 'Address is required' })}
                  label="Full Address *" multiline rows={2}
                  error={!!errors.address} helperText={errors.address?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <SelectField name="state" label="State *" options={INDIAN_STATES} control={control} required error={errors.state?.message} />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('district', { required: 'District is required' })}
                  label="District *"
                  error={!!errors.district} helperText={errors.district?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormInput
                  {...register('pinCode', { required: 'Pin code is required', pattern: { value: /^[0-9]{6}$/, message: 'Must be 6 digits' } })}
                  label="Pin Code *"
                  error={!!errors.pinCode} helperText={errors.pinCode?.message}
                />
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3, py: 2,
              background: '#fff',
              borderTop: '1px solid #e0e0e0',
              justifyContent: 'space-between',
            }}
          >
            <Typography fontSize={12} color="textSecondary">* Required fields</Typography>
            <Box display="flex" gap={1.5}>
              <Button onClick={handleClose} sx={{ fontSize: 14, color: BRAND_ALPHA, px: 3 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isAddingStudent || isUpdatingStudent}
                sx={{
                  fontSize: 14, px: 4,
                  background: `linear-gradient(135deg, ${BRAND} 0%, #6b6d75 100%)`,
                  borderRadius: 2, minWidth: 130,
                  boxShadow: '0 4px 12px rgba(69,71,81,0.3)',
                }}
              >
                {isAddingStudent || isUpdatingStudent
                  ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                  : isEditing ? 'Update Student' : 'Submit'}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
           VIEW STUDENT DIALOG
          ═══════════════════════════════════════════════════════════ */}
      <Dialog open={!!viewStudent} onClose={() => setViewStudent(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: `linear-gradient(135deg, ${BRAND} 0%, #6b6d75 100%)`, color: '#fff', py: 2, px: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography fontWeight={700} fontSize={17}>Student Details</Typography>
            <IconButton sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.15)' } }} onClick={() => setViewStudent(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {viewStudent && (
            <Box>
              {/* Profile banner */}
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${BRAND}15 0%, #f0f0f0 100%)`,
                  p: 3, display: 'flex', alignItems: 'center', gap: 2.5,
                }}
              >
                <Avatar
                  src={viewStudent.photo?.url}
                  sx={{ width: 80, height: 80, border: `3px solid ${BRAND_ALPHA}`, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                />
                <Box>
                  <Typography fontWeight={700} fontSize={20} color={BRAND}>
                    {viewStudent.title} {viewStudent.applicantName}
                  </Typography>
                  <Typography fontSize={13} color="textSecondary" sx={{ mt: 0.3 }}>{viewStudent.email}</Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={viewStudent.course || '—'} size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 11 }} />
                    <Chip label={viewStudent.gender} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                    <Chip label={viewStudent.category} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 2.5 }}>
                {/* Sections */}
                {[
                  {
                    label: 'Personal', color: BRAND_ALPHA,
                    rows: [
                      ['Guardian', viewStudent.guardianName],
                      ['Mother', viewStudent.motherName],
                      ['Relationship', viewStudent.relationship],
                      ['Date of Birth', viewStudent.dateOfBirth ? new Date(viewStudent.dateOfBirth).toLocaleDateString('en-IN') : null],
                    ],
                  },
                  {
                    label: 'Admission', color: '#2e7d32',
                    rows: [
                      ['Date of Admission', viewStudent.dateOfAdmission ? new Date(viewStudent.dateOfAdmission).toLocaleDateString('en-IN') : null],
                      ['Batch', viewStudent.batch],
                      ['Fees', viewStudent.fees ? `₹${viewStudent.fees}` : null],
                    ],
                  },
                  {
                    label: 'Contact', color: '#e65100',
                    rows: [
                      ['Mobile', viewStudent.mobile],
                      ['Guardian Mobile', viewStudent.mobileGuardian],
                    ],
                  },
                  {
                    label: 'Identity', color: '#6a1b9a',
                    rows: [
                      ['Aadhaar', viewStudent.aadhaarNumber],
                      ['Education', viewStudent.education],
                    ],
                  },
                  {
                    label: 'Address', color: '#00695c',
                    rows: [
                      ['Address', viewStudent.address],
                      ['State', viewStudent.state],
                      ['District', viewStudent.district],
                      ['Pin Code', viewStudent.pinCode],
                    ],
                  },
                ].map((section, si) => (
                  <Box key={si} mb={2}>
                    <Typography
                      fontSize={11} fontWeight={700}
                      sx={{ color: section.color, textTransform: 'uppercase', letterSpacing: 1, mb: 0.5, pl: 1, borderLeft: `3px solid ${section.color}` }}
                    >
                      {section.label}
                    </Typography>
                    {section.rows.map(([label, value]) => (
                      <DetailRow key={label} label={label} value={value} highlight={si % 2 === 0} />
                    ))}
                    {si < 4 && <Divider sx={{ mt: 1.5 }} />}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setViewStudent(null)} sx={{ fontSize: 14, color: BRAND_ALPHA }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteStudentId} onClose={() => setDeleteStudentId(null)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Delete Student
            <IconButton edge="end" color="inherit" onClick={() => setDeleteStudentId(null)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          <Typography>Are you sure you want to delete this student registration? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDeleteStudentId(null)} sx={{ fontSize: 13, color: BRAND_ALPHA }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={isDeletingStudent} sx={{ fontSize: 13, borderRadius: 2 }}>
            {isDeletingStudent ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CenterStudentManagement;
